import React, { useState, useMemo } from 'react';
import { Box, Paper, Button, useTheme, Grid, Alert, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useBulkCreateOrgStructureAssignment } from '../../hooks/orgStructureAssignments/useBulkCreateOrgStructureAssignment';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useGetAcademicYears } from '../../hooks/academicYears/useGetAcademicYears';
import { useGetAcademicTerms } from '../../hooks/academicTerms/useGetAcademicTerms';
import { useGetOrgStructureItemsTree } from '../../hooks/orgStructureItems/useGetOrgStructureItemsTree';
import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import { getEmployeesForSelect } from '../../api/apiEmployees';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';
import { useSnackbar } from 'notistack';
import i18next from 'i18next';

const FormBulkOrgStructureAssignment = () => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    const isDark = theme.palette.mode === 'dark';
    const { enqueueSnackbar } = useSnackbar();

    const { bulkCreate, isPending } = useBulkCreateOrgStructureAssignment();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);
    const { academicYears } = useGetAcademicYears(1, 999);
    const { academicTerms } = useGetAcademicTerms(1, 999);
    const { treeItems: orgStructureItems } = useGetOrgStructureItemsTree();
    const { departments } = useGetDepartments(1, 999);

    const [form, setForm] = useState({
        school_id: '', academic_year_id: '', academic_term_id: '',
        organizational_structure_item_id: '', department_id: '', starts_at: '',
    });
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);

    // Load employees with job_title + exclusion filters
    const empFilters = useMemo(() => ({
        school_id: form.school_id || undefined,
        department_id: form.department_id || undefined,
        exclude_assigned_item_id: form.organizational_structure_item_id || undefined,
        exclude_assigned_school_id: form.school_id || undefined,
        exclude_assigned_year_id: form.academic_year_id || undefined,
    }), [form.school_id, form.department_id, form.organizational_structure_item_id, form.academic_year_id]);

    const canLoadEmployees = !!(form.school_id && form.academic_year_id && form.organizational_structure_item_id);

    const { data: empSelectData, isLoading: empsLoading } = useQuery({
        queryKey: ["employeesForSelectBulk", empFilters, i18next.language],
        queryFn: () => getEmployeesForSelect(empFilters),
        enabled: canLoadEmployees,
    });

    // for-select returns [{ value, label }]
    const empData = empSelectData?.data || [];
    const empOpts = Array.isArray(empData)
        ? empData.map((e) => ({ id: e.value, name: e.label }))
        : [];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleEmployeeSelect = (e) => {
        setSelectedEmployeeIds(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedEmployeeIds.length) {
            enqueueSnackbar(isArabic ? 'اختر موظف واحد على الأقل' : 'Select at least one employee', { variant: 'error' });
            return;
        }

        bulkCreate({
            school_id: Number(form.school_id),
            academic_year_id: Number(form.academic_year_id),
            academic_term_id: form.academic_term_id ? Number(form.academic_term_id) : null,
            organizational_structure_item_id: Number(form.organizational_structure_item_id),
            employee_ids: selectedEmployeeIds.map(Number),
            starts_at: form.starts_at || null,
        });
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOpts = (schools || []).map((s) => ({ id: s.id, name: s.name }));
    const yearOpts = (academicYears || [])
        .filter((y) => !form.school_id || y.school_id === Number(form.school_id))
        .map((y) => ({ id: y.id, name: y.name }));
    const termOpts = (academicTerms || [])
        .filter((t) => !form.academic_year_id || t.academic_year_id === Number(form.academic_year_id))
        .map((t) => ({ id: t.id, name: t.name }));
    const itemOpts = (orgStructureItems || [])
        .filter((i) => i.allows_multiple)
        .map((i) => ({ id: i.id, name: i.name }));
    const deptOpts = [
        { id: '', name: isArabic ? 'الكل' : 'All' },
        ...(departments || [])
            .filter((d) => !form.school_id || d.school_id === Number(form.school_id))
            .map((d) => ({ id: d.id, name: d.name })),
    ];

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                {isArabic
                    ? 'تعيين مجموعة موظفين على نفس المنصب مرة واحدة. الموظفين المعيّنين بالفعل يتم استبعادهم تلقائياً.'
                    : 'Assign multiple employees to the same position at once. Already-assigned employees are automatically excluded.'}
            </Alert>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المدرسة' : 'School'} type="select" name="school_id"
                            value={form.school_id}
                            onChange={(e) => { setForm((p) => ({ ...p, school_id: e.target.value, academic_year_id: '', academic_term_id: '' })); setSelectedEmployeeIds([]); }}
                            options={schoolOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'السنة الدراسية' : 'Academic Year'} type="select" name="academic_year_id"
                            value={form.academic_year_id}
                            onChange={(e) => { setForm((p) => ({ ...p, academic_year_id: e.target.value, academic_term_id: '' })); setSelectedEmployeeIds([]); }}
                            options={yearOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الفصل الدراسي (اختياري)' : 'Term (optional)'} type="select" name="academic_term_id"
                            value={form.academic_term_id}
                            onChange={(e) => setForm((p) => ({ ...p, academic_term_id: e.target.value }))}
                            options={[{ id: '', name: isArabic ? 'بدون فصل' : 'None' }, ...termOpts]} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المنصب التنظيمي' : 'Position'} type="select" name="organizational_structure_item_id"
                            value={form.organizational_structure_item_id}
                            onChange={(e) => { setForm((p) => ({ ...p, organizational_structure_item_id: e.target.value })); setSelectedEmployeeIds([]); }}
                            options={itemOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'القسم' : 'Department'} type="select" name="department_id"
                            value={form.department_id}
                            onChange={(e) => { setForm((p) => ({ ...p, department_id: e.target.value })); setSelectedEmployeeIds([]); }}
                            options={deptOpts} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'تاريخ البداية' : 'Starts At'} type="date" name="starts_at"
                            value={form.starts_at} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12}>
                        {!canLoadEmployees ? (
                            <Alert severity="warning" variant="outlined">
                                {isArabic
                                    ? 'اختر المدرسة والسنة الدراسية والمنصب أولاً لعرض الموظفين المتاحين.'
                                    : 'Select school, academic year, and position first to see available employees.'}
                            </Alert>
                        ) : empsLoading ? (
                            <Typography color="text.secondary">{isArabic ? 'جاري تحميل الموظفين...' : 'Loading employees...'}</Typography>
                        ) : empOpts.length === 0 ? (
                            <Alert severity="success" variant="outlined">
                                {isArabic
                                    ? 'جميع الموظفين معيّنين بالفعل على هذا المنصب في هذه المدرسة والسنة.'
                                    : 'All employees are already assigned to this position for this school and year.'}
                            </Alert>
                        ) : (
                            <>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {isArabic ? `الموظفون المتاحون (${empOpts.length})` : `Available Employees (${empOpts.length})`}
                                </Typography>
                                <FormInput
                                    label={isArabic ? 'اختر الموظفين' : 'Select Employees'}
                                    type="multi_select"
                                    name="employee_ids"
                                    selectedValue={selectedEmployeeIds}
                                    handleSelectChange={handleEmployeeSelect}
                                    options={empOpts}
                                    required={false}
                                />
                                {selectedEmployeeIds.length > 0 && (
                                    <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
                                        {isArabic ? `تم اختيار ${selectedEmployeeIds.length} موظف` : `${selectedEmployeeIds.length} selected`}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary"
                        disabled={isPending || !selectedEmployeeIds.length}>
                        {isPending
                            ? (isArabic ? 'جاري التعيين...' : 'Assigning...')
                            : (isArabic ? `تعيين ${selectedEmployeeIds.length || ''} موظف` : `Assign ${selectedEmployeeIds.length || ''} Employees`)}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormBulkOrgStructureAssignment;
